// Eskom PARCC — client for the privileged admin service (admin-api on Cloud Run).
//
// Admin WRITES (grant access, change role, revoke, create credentials) never go
// through the Data Connect client SDK — they go to the same-origin `/api/admin/*`
// endpoints, which verify the caller's `admin` custom claim server-side. We attach
// the current user's Firebase ID token; the service is the real authorization
// boundary (the React route guards are UX only).
import { auth } from './firebase';
import type { Role } from './access';
import { assertWritable } from './readOnly';

export interface GrantInput {
  email: string;
  role: Role;
  /** Home division (teamLead/reviewer); null/omitted for cross-division roles. */
  division?: string | null;
  /** Allocation flag (manager only — Greg yes / George no). */
  canAllocate?: boolean;
  /** Cross-division visibility for a reviewer/team lead (Lindiwe R4 #3). */
  crossDivision?: boolean;
  /** AI Compliance Engine eligibility (TK Jul 2026). Omitted/false = no AI. */
  aiEnabled?: boolean;
  displayName?: string | null;
  firstName?: string | null;
  lastName?: string | null;
}

export interface CreateCredentialsInput {
  email: string;
  displayName?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  role?: Role;
  division?: string | null;
  canAllocate?: boolean;
  crossDivision?: boolean;
  tempPassword?: string;
}

/** One AI proposal for a single rubric criterion. Mirrors the validated shape
 *  admin-api/aiGuard.js emits — anything it could not evidence never gets here. */
export interface AiProposal {
  /** The criterion's 1-based position in the template. Joined on `seq`, never on
   *  `code`: the template generator mis-parses codes like "1.6" into "1".
   *
   *  NULL for a holistic finding — one the engine raised outside the checklist,
   *  which by definition attaches to no criterion. */
  seq: number | null;
  /** True when this is a finding raised outside the rubric's criteria (the
   *  "Additional Comments" / "Gaps Identified" columns). Explicit rather than
   *  inferred from a null seq, so a malformed proposal can never become one. */
  holistic?: boolean;
  /** One of CES_STATUS_OPTIONS — the reviewer's own vocabulary. */
  verdict: string;
  confidence: number;
  rationale: string;
  citations: {
    doc: string;
    /** DERIVED from where the quote actually sits in the extracted text, not
     *  taken from the model — the OCR cache is a flat blob with no page markers,
     *  so anything the engine claimed here was an inference. */
    page: number;
    quote: string;
    /** 'derived' when a layout sidecar resolved the page; 'claimed' for a
     *  document OCR'd before layout capture, so "verified" and "asserted" stay
     *  distinguishable. */
    pageSource?: 'derived' | 'claimed';
    /** The page the engine claimed, recorded only when it disagreed. */
    pageClaimed?: number;
    /** Highlight rectangles for the quoted span, normalised 0..1 against the
     *  page. Resolved server-side from the same offsets that derived the page,
     *  so the highlight and the page number cannot point at different things. */
    boxes?: { x0: number; y0: number; x1: number; y1: number }[];
  }[];
  pfmaSection: string | null;
  /** True when no evidence was located, which defaults the verdict to PFMA. */
  missingEvidence: boolean;
  /** Any document in this proposal's CONTEXT contained instruction-like text.
   *  Context-scoped, not citation-scoped: an injection in document A can tell
   *  the model to cite clean document B, so checking only the cited document
   *  misses exactly the attack this flag exists to catch. */
  suspiciousEvidence: boolean;
  /** Which documents were flagged, so the reviewer knows what to open. */
  suspiciousDocuments?: string[];
}

/** One row of the live trace. `call` is the mono line, `detail` the plain-English
 *  one beside it. `detail` carries metadata only — never evidence text. */
export interface AiRunStep {
  id: string;
  batch: number;
  call: string;
  detail: string;
  status: 'pending' | 'running' | 'ok' | 'warn' | 'error';
  startedAt: string | null;
  finishedAt: string | null;
}

/**
 * A review run, as the server projects it to the client.
 *
 * The server's own record additionally holds the frozen criteria list, the
 * evidence manifest and the owner's address; none of that is sent.
 */
export interface AiRun {
  schemaVersion: number;
  runId: string;
  referenceNo: string;
  status: 'running' | 'done' | 'failed' | 'cancelled';
  cancelRequested: boolean;
  cursor: number;
  totalBatches: number;
  batchSize: number;
  createdAt: string;
  updatedAt: string;
  finishedAt: string | null;
  elapsedMs: number;
  steps: AiRunStep[];
  proposals: AiProposal[];
  /** Criteria whose proposal was discarded, with why. Never silent. */
  rejected: { seq: number | null; reason: string }[];
  flaggedDocuments: string[];
  refused: boolean;
  /** PER BATCH, not per run: a quota refusal parks Claude and swaps to Gemini
   *  mid-run, so one model name would misattribute half the verdicts. */
  batchesRun: { index: number; model: string | null; provider: string | null; ms: number; outcome: string }[];
  /** Another caller holds the lease — read state, do not spend. */
  busy?: boolean;
  /** The step was aborted before it recorded anything; call /step again. */
  retryable?: boolean;
  documents?: number;
  /** A run the reviewer left to finish server-side: the client polls it but never
   *  drives it, the scheduler sweep does, and completion emails the owner. */
  background?: boolean;
  /** Fingerprint of the evidence pack + prompts + policy, so this run can later be
   *  reused if the pack is unchanged. */
  packHash?: string | null;
  /** Criteria reserved for the human (mode reviewer/none): not planned, batched or
   *  billed. Shown as a "Reviewer to perform" row and excluded from coverage. */
  reviewerOnly?: { seq: number; code: string | null; label: string; mode: string }[];
}

/**
 * The durable, QUOTE-FREE projection of a completed review. Returned by
 * `aiReviewLatest`, and by `aiReviewStart` (inside `{ reused }`) when an unchanged
 * pack lets an earlier review be reused. `proposalsJson` / `holisticJson` are
 * `AiProposal[]` serialised with the verbatim quote stripped from every citation
 * (doc / page / pageSource / boxes are kept, so a citation still opens and
 * highlights; only the lifted text is gone).
 */
export interface AiReviewResult {
  referenceNo: string;
  packHash: string;
  runId: string;
  model: string | null;
  status: string;
  proposalsJson: string;
  holisticJson: string | null;
  proposalCount: number | null;
  /** Quote-free coverage accounting {plannedSeqs, proposedSeqs, rejected,
   *  reviewerOnly, batches}, so a rehydrated/reused review still shows what the
   *  engine did NOT assess. Absent on results written before this shipped. */
  accountingJson?: string | null;
  plannedCount?: number | null;
  createdAt: string;
  finishedAt: string | null;
}

/** What `aiReviewStart` resolves to: a freshly planned run, or a reuse offer. */
export type AiStartResult = AiRun | { reused: true; result: AiReviewResult };

/** One transaction's newest completed review, in the cross-transaction AI
 *  assurance feed. Quote-free (proposalsJson never held verbatim quotes). */
export interface AiReviewSummaryRow {
  referenceNo: string;
  model: string | null;
  /** JSON `AiProposal[]` (per-criterion verdicts), quote-free. */
  proposalsJson: string;
  proposalCount: number | null;
  createdAt: string;
}

export interface CreateCredentialsResult {
  ok: true;
  uid: string;
  /** The temporary password the admin conveys to the new user out-of-band. */
  tempPassword: string;
}

export interface SetPasswordInput {
  email: string;
  /** A specific password to set (>= 8 chars). Omit to have one generated. */
  password?: string;
}

export interface SetPasswordResult {
  ok: true;
  uid: string;
  /** The password that was set — convey it to the user out-of-band. */
  password: string;
  /** True when no account existed yet and one was provisioned (vs a reset). */
  created: boolean;
}

/** A base64-encoded report attachment (PDF/PPTX) for the email-report endpoint. */
export interface ReportAttachment {
  filename: string;
  /** MIME type, e.g. application/pdf or application/vnd.openxmlformats-…presentation. */
  mime: string;
  /** Base64 body (no `data:` prefix). */
  b64: string;
}

export interface EmailReportInput {
  recipientEmail: string;
  periodTitle?: string;
  scopeLabel?: string;
  attachments: ReportAttachment[];
}

/** POST JSON to an admin endpoint with the caller's Firebase ID token attached.
 *  Blocked while an admin is previewing as another role (read-only preview). */
async function authedPost<T>(path: string, body: unknown): Promise<T> {
  assertWritable();
  const user = auth.currentUser;
  if (!user) {
    throw new Error('You must be signed in to perform this action.');
  }
  const token = await user.getIdToken();
  const res = await fetch(path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify(body),
  });
  // The service returns { error } on failure; surface it, falling back to status.
  const data: unknown = await res.json().catch(() => ({}));
  if (!res.ok) {
    const message =
      typeof data === 'object' && data !== null && 'error' in data
        ? String((data as { error: unknown }).error)
        : `Request failed (${res.status}).`;
    throw new Error(message);
  }
  return data as T;
}

/** GET an endpoint with the caller's Firebase ID token attached. */
async function authedGet<T>(path: string): Promise<T> {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('You must be signed in to perform this action.');
  }
  const token = await user.getIdToken();
  const res = await fetch(path, { headers: { Authorization: `Bearer ${token}` } });
  const data: unknown = await res.json().catch(() => ({}));
  if (!res.ok) {
    const message =
      typeof data === 'object' && data !== null && 'error' in data
        ? String((data as { error: unknown }).error)
        : `Request failed (${res.status}).`;
    throw new Error(message);
  }
  return data as T;
}

/** PATCH an endpoint with the caller's Firebase ID token attached. */
async function authedPatch<T>(path: string, body: unknown): Promise<T> {
  assertWritable();
  const user = auth.currentUser;
  if (!user) {
    throw new Error('You must be signed in to perform this action.');
  }
  const token = await user.getIdToken();
  const res = await fetch(path, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify(body),
  });
  const data: unknown = await res.json().catch(() => ({}));
  if (!res.ok) {
    const message =
      typeof data === 'object' && data !== null && 'error' in data
        ? String((data as { error: unknown }).error)
        : `Request failed (${res.status}).`;
    throw new Error(message);
  }
  return data as T;
}

/** PUT an endpoint with the caller's Firebase ID token attached. */
async function authedPut<T>(path: string, body: unknown): Promise<T> {
  assertWritable();
  const user = auth.currentUser;
  if (!user) {
    throw new Error('You must be signed in to perform this action.');
  }
  const token = await user.getIdToken();
  const res = await fetch(path, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify(body),
  });
  const data: unknown = await res.json().catch(() => ({}));
  if (!res.ok) {
    const message =
      typeof data === 'object' && data !== null && 'error' in data
        ? String((data as { error: unknown }).error)
        : `Request failed (${res.status}).`;
    throw new Error(message);
  }
  return data as T;
}

/** DELETE an endpoint with the caller's Firebase ID token attached. */
async function authedDelete<T>(path: string): Promise<T> {
  assertWritable();
  const user = auth.currentUser;
  if (!user) {
    throw new Error('You must be signed in to perform this action.');
  }
  const token = await user.getIdToken();
  const res = await fetch(path, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${token}` },
  });
  const data: unknown = await res.json().catch(() => ({}));
  if (!res.ok) {
    const message =
      typeof data === 'object' && data !== null && 'error' in data
        ? String((data as { error: unknown }).error)
        : `Request failed (${res.status}).`;
    throw new Error(message);
  }
  return data as T;
}

/** The signed-in user's scoped register (raw Data Connect rows; the caller maps them). */
/** One rolling snapshot run, as listed by /api/admin/snapshots (WS4). */
export interface SnapshotRunRow {
  id: string;
  takenAt: string;
  trigger: 'auto' | 'manual' | 'pre-restore' | string;
  status: 'running' | 'ok' | 'failed' | string;
  storagePath: string | null;
  schemaVersion: number | null;
  counts: Record<string, number> | null;
  contentHash: string | null;
  bytes: number | null;
  byEmail: string | null;
  error: string | null;
  finishedAt: string | null;
}

/** The per-table would-change counts a restore dry-run reports (WS4). */
export interface RestorePlanSummary {
  transactions: { update: number; insert: number; unchanged: number; extrasUntouched: number };
  workingPapers: { update: number; insert: number; unchanged: number; extrasUntouched: number };
  cesRows: {
    replaceTransactions: string[];
    rowsToWrite: number;
    unchangedTransactions: number;
    extraTransactionsUntouched: number;
  };
  findings: {
    replaceTransactions: string[];
    rowsToWrite: number;
    unchangedTransactions: number;
    extraTransactionsUntouched: number;
  };
  qaNotes: { update: number; insert: number; unchanged: number; extrasUntouched: number };
  rfiRegister: { replace: boolean; rows: number };
  rtmSignoff: { upsert: number; unchanged: number };
  samples: {
    transactionsUpdate: string[];
    transactionsInsert: string[];
    findingsReplace: string[];
  };
}

/** One population-removal batch, as listed by /api/admin/removal-batches (WS3). */
export interface RemovalBatchRow {
  id: string;
  at: string;
  actorEmail: string | null;
  txCount: number | null;
  findingCount: number | null;
  note: string | null;
  restoredAt: string | null;
  restoredBy: string | null;
}

export interface ScopedRegisterResponse {
  transactions: unknown[];
  findings: unknown[];
  workingPapers: unknown[];
}

/** A reviewer the caller may allocate to (server-scoped to the caller's division). */
export interface AllocatableReviewer {
  email: string;
  displayName: string | null;
  firstName: string | null;
  lastName: string | null;
  role: Role;
  division: string | null;
  active: boolean;
}

/** Outcome of a server-enforced allocation: how many rows were assigned vs skipped
 *  (skipped = outside the caller's allocation scope). */
export interface AllocateResult {
  ok: true;
  assigned: number;
  skipped: number;
}

/** The editable self-service profile fields (display name + extended attributes).
 *  The `ui*` / regional fields are the Settings screen's workspace preferences —
 *  written by lib/uiPrefs so a person's theme follows them across devices. The
 *  server whitelists every one of them, so an unknown value is stored as null. */
export interface ProfileInput {
  displayName?: string | null;
  photoUrl?: string | null;
  jobTitle?: string | null;
  phone?: string | null;
  department?: string | null;
  /** 'legacy' | 'lumen' */
  uiVariant?: string | null;
  /** 'light' | 'dark' */
  uiTheme?: string | null;
  /** 'comfort' | 'balanced' | 'compact' */
  uiDensity?: string | null;
  /** 'expanded' | 'collapsed' */
  sidebarDefault?: string | null;
  /** IANA zone, e.g. 'Africa/Johannesburg'. */
  timeZone?: string | null;
  /** BCP-47 locale for dates + number grouping, e.g. 'en-ZA'. */
  numberFormat?: string | null;
  /** 'off' | 'on' — the person's own AI switch. A preference, not a permission. */
  aiMode?: string | null;
}

/** The personal-detail subset the Account modal owns. The workspace-preference
 *  fields are written separately by lib/uiPrefs; the endpoint merges partial
 *  patches, so the two writers never clobber each other. */
export type AccountProfileInput = Pick<
  ProfileInput,
  'displayName' | 'photoUrl' | 'jobTitle' | 'phone' | 'department'
>;

/** The signed-in user's profile as returned by the account endpoint. Every
 *  writable column comes back, unset ones as `null`. */
export type ProfileResponse = { email: string } & {
  [K in keyof Required<ProfileInput>]: string | null;
};

/**
 * The caller's own notification EVENT switches — which classes of alert reach
 * them at all. Distinct from the admin-controlled in-app/email CHANNEL pair on
 * the Notifications screen. Every value is definite (the server applies the
 * fail-open rule when reading, so `null` columns come back as `true`).
 */
export interface NotificationEventPrefs {
  /** Transactions allocated to me. */
  notifyAllocations: boolean;
  /** A manager sends one of my working papers back for rework. */
  notifyRevisions: boolean;
  /** Replies and resolutions on my papers. */
  notifyQaNotes: boolean;
  /** Digest when reviews in my division complete. */
  notifyReviewDigest: boolean;
  /** Email-digest cadence — the calm replacement for per-transaction emails
   *  (owner feedback Aug 2026). The GET returns the effective value (null → weekly). */
  digestCadence: DigestCadence;
}

/** How often the batched email digest is delivered. */
export type DigestCadence = 'off' | 'daily' | 'weekly' | 'both';

/** Proxy responses for the audit-trail reads. Rows are raw Data Connect shapes
 *  (mapped in db.ts via mapAuditRow / mapSnapshotSummaryRow); typed loosely here to
 *  avoid a db.ts↔adminApi.ts import cycle. */
interface TransactionHistoryResponse {
  ok: true;
  referenceNo: string;
  entries: unknown[];
  snapshots: unknown[];
}
interface EvaluationSnapshotResponse {
  ok: true;
  snapshots: unknown[];
}
interface AuditFeedResponse {
  entries: unknown[];
  truncated: boolean;
}

/** A persisted RtmReport row as returned by the admin-api (JSON columns as parsed values). */
export interface RtmReportRow {
  id: string;
  reportKey: string;
  fy: string;
  quarter: string;
  division: string;
  status: string;
  title: string | null;
  reportDate: string | null;
  addresseesJson: unknown;
  acknowledgersJson: unknown;
  signatoryName: string | null;
  signatoryTitle: string | null;
  coverLetterJson: unknown;
  populationInitial: number | null;
  populationFinal: number | null;
  populationNote: string | null;
  classOverridesJson: unknown;
  table1OverridesJson: unknown;
  overallRating: number | null;
  conclusionText: string | null;
  managementResponse: string | null;
  keyRecommendationsJson: unknown;
  unclosedCountOverride: number | null;
  ieCountOverride: number | null;
  ieValueOverride: number | null;
  issuedReportNumber: string | null;
  issuedAt: string | null;
  issuedBy: string | null;
  docxStoragePath: string | null;
  pdfStoragePath: string | null;
  modelStoragePath: string | null;
  rowVersion: number;
  deleted: boolean;
  createdAt: string | null;
  updatedAt: string | null;
  updatedBy: string | null;
}

/** A persisted RtmFinding row as returned by the admin-api. */
export interface RtmFindingRowRead {
  id: string;
  reportId: string;
  seq: number;
  number: string | null;
  heading: string | null;
  classification: string | null;
  findingTitle: string | null;
  rating: number | null;
  tier: string | null;
  criteria: string | null;
  observation: string | null;
  risk: string | null;
  summaryObservation: string | null;
  subObservationsJson: unknown;
  rootCausePrimary: string | null;
  rootCauseSecondary: string | null;
  rootCauseText: string | null;
  recommendationsJson: unknown;
  linkedRefsJson: unknown;
  linkedCriteriaJson: unknown;
  autoDraftJson: unknown;
  rowVersion: number;
  deleted: boolean;
  updatedAt: string | null;
  updatedBy: string | null;
}

/** A per-division RTM profile row. */
export interface RtmProfileRow {
  division: string;
  longName: string | null;
  addresseesJson: unknown;
  acknowledgersJson: unknown;
  signatoryName: string | null;
  signatoryTitle: string | null;
  classMapJson: unknown;
  updatedAt: string | null;
  updatedBy: string | null;
}

export const adminApi = {
  /** Server-scoped register for the signed-in user (owner feedback point 7). */
  scopedRegister: () => authedGet<ScopedRegisterResponse>('/api/scoped/register'),
  /** One transaction's audit trail via the scoped proxy (non-admins). 404s (as
   *  "not found") when the caller may not see the transaction. */
  transactionHistory: (referenceNo: string) =>
    authedGet<TransactionHistoryResponse>(
      `/api/scoped/transaction-history?referenceNo=${encodeURIComponent(referenceNo)}`,
    ),
  /** CES snapshot blobs (≤2) via the scoped proxy — snapshots the caller cannot
   *  see are dropped server-side. */
  evaluationSnapshots: (ids: readonly string[]) =>
    authedGet<EvaluationSnapshotResponse>(
      `/api/scoped/evaluation-snapshot?ids=${encodeURIComponent(ids.join(','))}`,
    ),
  /** The global audit feed for [from, to] via the scoped proxy (transaction-scoped
   *  for a division-bound caller, plus rows they authored). */
  auditFeed: (from: string, to: string) =>
    authedGet<AuditFeedResponse>(
      `/api/scoped/audit-feed?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`,
    ),
  /** Read the signed-in user's own profile (server-verified identity). */
  getProfile: () => authedGet<ProfileResponse>('/api/account/profile'),
  /** Update the signed-in user's own profile (Auth + DB + audit, server-side).
   *  PATCH semantics: only the keys you send are written. */
  updateProfile: (input: ProfileInput) => authedPost<{ ok: true }>('/api/account/profile', input),
  /** The caller's own notification EVENT switches (Settings → My notifications). */
  getMyNotificationPrefs: () =>
    authedGet<NotificationEventPrefs>('/api/account/notification-prefs'),
  /** Save the caller's own notification EVENT switches. Self-scoped server-side:
   *  the grant key is taken from the verified token, never from this body. */
  setMyNotificationPrefs: (prefs: NotificationEventPrefs) =>
    authedPost<{ ok: true }>('/api/account/notification-prefs', prefs),
  /** Set the caller's own email-digest cadence. Self-scoped server-side; kept
   *  separate from the event switches so neither write clobbers the other. */
  setDigestCadence: (digestCadence: DigestCadence) =>
    authedPost<{ ok: true }>('/api/account/digest-cadence', { digestCadence }),
  /** Revoke every session for the caller (Settings → Sign out of all sessions).
   *  The caller should sign out locally straight afterwards. */
  revokeMySessions: () => authedPost<{ ok: true }>('/api/account/revoke-sessions', {}),
  /**
   * Set a transaction's FINANCIAL YEAR (Lindiwe, 16 Sep 2026). `year` is insert-only
   * at the connector, so Manage / Bulk Update cannot write it through the normal
   * update mutation; this routes it through the admin service, which writes the raw
   * `transaction_update` + an audit row. Send the CANONICAL form: the caller normalises
   * first (Manage folds "F27" → "FY27"), and the server accepts only `FYnn`, rejecting
   * anything else with a 400. Admin-only: the `/api/admin` router's requireAdmin (the
   * Manage screen is admin-only too).
   */
  setTransactionYear: (referenceNo: string, year: string) =>
    authedPost<{ ok: true; year: string }>('/api/admin/transaction-year', { referenceNo, year }),

  // --- AI Compliance Engine ------------------------------------------------
  // Same-origin `/api/*` throughout, so these inherit the Hosting rewrite and
  // need no CORS and no client env var. The server re-derives AI eligibility
  // from the caller's AccessGrant on every call — the client's AI Mode
  // preference is never trusted, and forcing it on in devtools changes nothing.
  aiChat: (input: { message: string }) =>
    authedPost<{ ok: true; text: string; citations?: { doc: string; page: number }[] }>(
      '/api/ai/chat',
      input,
    ),

  // The review is a POLLED JOB, not one request. A full rubric is several
  // minutes of model work and the Hosting `/api/**` rewrite gives up at ~60s, so
  // a single call would complete its work, bill for it, and hand the browser a
  // gateway timeout. One `step` = one batch = one model call.
  //
  // Returns proposals throughout; the reviewer's existing Save remains the only
  // writer.

  /** A short-lived signed URL for one cited evidence document.
   *  The URL is a bearer token, so the server re-derives the caller's grant and
   *  checks the document belongs to this transaction before issuing it. */
  aiEvidenceUrl: (referenceNo: string, doc: string) =>
    authedGet<{ ok: true; url: string; expiresInMs: number }>(
      `/api/ai/evidence/url?referenceNo=${encodeURIComponent(referenceNo)}&doc=${encodeURIComponent(doc)}`,
    ),

  /** Plan a run. Does no model work — cheap and fast. Returns a run, OR, when an
   *  identical completed review already exists (unchanged pack) and `force` was
   *  not set, `{ reused: true, result }` — no run is minted and nothing is spent.
   *  `background: true` marks a run for the server sweep to finish and to email on
   *  completion; the client then does not drive it. */
  aiReviewStart: (input: {
    referenceNo: string;
    criteria: { seq: number; label: string }[];
    background?: boolean;
    force?: boolean;
  }) => authedPost<AiStartResult>('/api/ai/review/start', input),

  /** The newest COMPLETED review for a transaction — durable and quote-free, so
   *  the panel can rehydrate an earlier review on a fresh browser without spend. */
  aiReviewLatest: (referenceNo: string) =>
    authedGet<{ result: AiReviewResult | null }>(
      `/api/ai/review/latest?referenceNo=${encodeURIComponent(referenceNo)}`,
    ),

  /** Every transaction's newest completed review — the AI Assurance feed. Report
   *  leads and above only (crosses divisions). */
  aiReviewLatestAll: () =>
    authedGet<{ results: AiReviewSummaryRow[] }>('/api/ai/review/latest-all'),

  /** Run exactly ONE batch. This is the call that spends money. */
  aiReviewStep: (input: { referenceNo: string; runId: string }) =>
    authedPost<AiRun>('/api/ai/review/step', input),

  /** Read a run without running anything — how the client recovers a lost
   *  response and rehydrates after a remount. */
  aiReviewRun: (referenceNo: string, runId: string) =>
    authedGet<AiRun>(
      `/api/ai/review/run?referenceNo=${encodeURIComponent(referenceNo)}&runId=${encodeURIComponent(runId)}`,
    ),

  /** Ask a run to stop at the next batch boundary. */
  aiReviewCancel: (input: { referenceNo: string; runId: string }) =>
    authedPost<AiRun>('/api/ai/review/cancel', input),
  /** Grant (or re-grant) access to an email at a role. */
  grant: (input: GrantInput) =>
    authedPost<{ ok: true; claimApplied: boolean }>('/api/admin/grant', input),
  /** Change an existing grant's role. */
  setRole: (email: string, role: Role) =>
    authedPost<{ ok: true; claimApplied: boolean }>('/api/admin/role', { email, role }),
  /** Revoke access (deactivate grant, disable account, clear claim). */
  revoke: (email: string) => authedPost<{ ok: true }>('/api/admin/revoke', { email }),
  /** Set a person's notification switches (TK Jul 2026 — Notifications screen).
   *  Both flags are definite: the screen always writes the full state. */
  setNotificationPrefs: (email: string, prefs: { notifyInApp: boolean; notifyEmail: boolean }) =>
    authedPost<{ ok: true }>('/api/admin/notification-prefs', { email, ...prefs }),
  /** Provision a new email/password account and grant it. Returns the temp password. */
  createCredentials: (input: CreateCredentialsInput) =>
    authedPost<CreateCredentialsResult>('/api/admin/create-credentials', input),
  /** Set or reset the password for an existing user (idempotent — provisions the
   *  account if it doesn't exist yet). Returns the password to convey. */
  setPassword: (input: SetPasswordInput) =>
    authedPost<SetPasswordResult>('/api/admin/set-password', input),
  /** Remove the existing population (soft-delete into a removal batch). Returns
   *  the counts and the new batch id (WS3 — every removal is now individually
   *  restorable from the removal history). */
  purgePopulation: (note?: string) =>
    authedPost<{ ok: true; batchId: string; transactions: number; findings: number }>(
      '/api/admin/purge-population',
      { confirm: 'DELETE', ...(note?.trim() ? { note: note.trim() } : {}) },
    ),
  /** Restore ONE removal batch (un-delete exactly that batch's rows, as-was).
   *  `noop: true` = nothing left to restore in the batch (rows re-imported or
   *  re-removed since); the batch is NOT consumed. */
  restorePopulation: (batchId: string) =>
    authedPost<{
      ok: true;
      batchId: string;
      transactions: number;
      findings: number;
      noop?: boolean;
      message?: string;
    }>('/api/admin/restore-population', { confirm: 'RESTORE', batchId }),
  /** List removal batches, newest first, for the removal-history card. */
  listRemovalBatches: () =>
    authedGet<{ ok: true; batches: RemovalBatchRow[] }>('/api/admin/removal-batches'),
  /** List snapshot runs (newest first) + the last successful stamp (WS4). */
  snapshots: () =>
    authedGet<{ ok: true; runs: SnapshotRunRow[]; lastOkAt: string | null }>(
      '/api/admin/snapshots',
    ),
  /** Take a snapshot right now. */
  snapshotNow: () =>
    authedPost<{
      ok: true;
      snapshotId: string;
      storagePath: string;
      counts: Record<string, number>;
      bytes: number;
    }>('/api/admin/snapshot-now', {}),
  /** Dry-run a snapshot restore: the per-table would-change plan, nothing written. */
  restoreSnapshotDryRun: (snapshotId: string) =>
    authedPost<{
      ok: true;
      dryRun: true;
      snapshot: { id: string; takenAt: string; trigger: string };
      plan: RestorePlanSummary;
    }>('/api/admin/restore-snapshot', { snapshotId, dryRun: true }),
  /** Apply a snapshot restore (a pre-restore snapshot is taken automatically). */
  restoreSnapshot: (snapshotId: string) =>
    authedPost<{
      ok: true;
      restoreRunId: string;
      preSnapshotId: string | null;
      applied: Record<string, unknown>;
    }>('/api/admin/restore-snapshot', { snapshotId, confirm: 'RESTORE' }),

  // --- branded workflow notifications (best-effort; recipients resolved server-side) ---
  /** Notify the allocating admin AND the division's team lead(s) that a review is
   *  complete (recipients resolved server-side). */
  notifyReviewComplete: (referenceNo: string) =>
    authedPost<{ ok: true; notified?: number; sent?: number; skipped?: boolean }>(
      '/api/notify/review-complete',
      { referenceNo },
    ),
  /** Notify the assigned reviewer that a transaction was sent back for revision. */
  notifyRevisionRequest: (referenceNo: string, note?: string) =>
    authedPost<{ ok: true; sent?: boolean; skipped?: boolean }>('/api/notify/revision-request', {
      referenceNo,
      note: note ?? '',
    }),
  /** Notify each assigned reviewer of a fresh allocation (admin-only). */
  notifyAllocationAssigned: (referenceNos: string[]) =>
    authedPost<{ ok: true; reviewers: number; sent: number }>('/api/notify/allocation-assigned', {
      referenceNos,
    }),
  /** The reviewers the signed-in user may allocate to (server-scoped to division). */
  allocatableReviewers: () =>
    authedGet<{ reviewers: AllocatableReviewer[] }>('/api/allocatable-reviewers'),
  /** Allocate transactions to a reviewer — server-enforced division scope. The
   *  server skips any row outside the caller's scope and reports the counts. */
  allocate: (referenceNos: string[], reviewerEmail: string) =>
    authedPost<AllocateResult>('/api/allocate', { referenceNos, reviewerEmail }),
  /** Email a generated report (base64 PDF/PPTX attachments) to a provisioned user. */
  emailReport: (input: EmailReportInput) =>
    authedPost<{ ok: true; id?: string }>('/api/email/report', input),

  // --- QA notes + report archive (Jun 23 2026 feedback 4a / 2c) ---
  /** Add a QA note to a reviewer's working paper (manager/admin only — server-
   *  enforced). The reviewer is alerted (in-app notification + branded email). */
  notifyQaNote: (referenceNo: string, note: string) =>
    authedPost<{ ok: true; alerted: boolean }>('/api/notify/qa-note', { referenceNo, note }),
  /** Resolve (or re-open) a QA note (manager/admin only). */
  resolveQaNote: (id: string, resolved = true) =>
    authedPost<{ ok: true }>('/api/qa-note/resolve', { id, resolved }),
  /** Reserve the next report number for a {division, asOfDate} (server-assigned). */
  reportsNextNumber: (asOfDate: string, division?: string, kind?: 'deck' | 'rtm') =>
    authedGet<{ ok: true; reportNumber: string }>(
      `/api/reports/next-number?asOfDate=${encodeURIComponent(asOfDate)}${division ? `&division=${encodeURIComponent(division)}` : ''}${kind ? `&kind=${kind}` : ''}`,
    ),
  /** A division's archived report runs (team-lead-safe; server-scoped). */
  reportsDivisional: (division: string) =>
    authedGet<{ ok: true; reportRuns: unknown[] }>(
      `/api/reports/divisional?division=${encodeURIComponent(division)}`,
    ),

  // --- Report to Management (Phase 2 in-app lane; server-scoped, 404 on a miss) ---
  /** List the reports for a FY (optionally a quarter / division); scoped to the caller. */
  rtmReports: (fy: string, quarter?: string, division?: string) =>
    authedGet<{ ok: true; reports: RtmReportRow[] }>(
      `/api/rtm/reports?fy=${encodeURIComponent(fy)}${quarter ? `&quarter=${encodeURIComponent(quarter)}` : ''}${division ? `&division=${encodeURIComponent(division)}` : ''}`,
    ),
  /** One report + its ordered findings (404 if not visible to the caller). */
  rtmReport: (reportKey: string) =>
    authedGet<{ ok: true; report: RtmReportRow; findings: RtmFindingRowRead[] }>(
      `/api/rtm/report/${encodeURIComponent(reportKey)}`,
    ),
  /** Create a report from a client-computed auto-draft (idempotent on reportKey). */
  rtmCreateReport: (
    fy: string,
    quarter: string,
    division: string,
    report: unknown,
    findings: unknown[],
  ) =>
    authedPost<{ ok: true; report: RtmReportRow; findings: RtmFindingRowRead[]; existed?: boolean }>(
      '/api/rtm/report',
      { fy, quarter, division, report, findings },
    ),
  /** Save edits to a draft report's editable fields (read-merge-update server-side). */
  rtmPatchReport: (reportKey: string, report: unknown) =>
    authedPatch<{ ok: true; report: RtmReportRow; findings: RtmFindingRowRead[] }>(
      `/api/rtm/report/${encodeURIComponent(reportKey)}`,
      { report },
    ),
  /** Append a finding to a draft report. */
  rtmAddFinding: (reportKey: string, finding: unknown) =>
    authedPost<{ ok: true; findings: RtmFindingRowRead[] }>(
      `/api/rtm/report/${encodeURIComponent(reportKey)}/findings`,
      { finding },
    ),
  /** Edit a finding. */
  rtmPatchFinding: (id: string, finding: unknown) =>
    authedPatch<{ ok: true; findings: RtmFindingRowRead[] }>(`/api/rtm/finding/${encodeURIComponent(id)}`, {
      finding,
    }),
  /** Soft-delete a finding. */
  rtmDeleteFinding: (id: string) =>
    authedDelete<{ ok: true; findings: RtmFindingRowRead[] }>(`/api/rtm/finding/${encodeURIComponent(id)}`),
  /** The per-division RTM profile (defaults reused as each report's starting point). */
  rtmProfile: (division: string) =>
    authedGet<{ ok: true; profile: RtmProfileRow | null }>(
      `/api/rtm/profile/${encodeURIComponent(division)}`,
    ),
  /** Save the per-division RTM profile ("save as division default"). */
  rtmSaveProfile: (division: string, profile: unknown) =>
    authedPut<{ ok: true; profile: RtmProfileRow | null }>(
      `/api/rtm/profile/${encodeURIComponent(division)}`,
      { profile },
    ),
  /** Issue a report — the client renders the DOCX/PDF (base64) + frozen model; the
   *  server uploads them, reserves a PARCC-RTM number, records the run, and stamps
   *  the report issued (thereafter immutable). */
  rtmIssue: (
    reportKey: string,
    payload: { docxBase64: string; pdfBase64: string; model: unknown; asOfDate: string },
  ) =>
    authedPost<{ ok: true; report: RtmReportRow; reportNumber: string }>(
      `/api/rtm/report/${encodeURIComponent(reportKey)}/issue`,
      payload,
    ),
};
